import { AdvancedSelect } from '../../../../../widgets/select';
import { Button } from '../../../../../widgets/buttons';
import { Checkbox } from '../../../../../widgets/checkbox';
import { ColorPicker } from '../../../../../widgets/colorPicker';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import { useDialogManager } from '../../../../../widgets/dialog';
import { showSuccessNotification } from '../../../../../widgets/notificationToaster/notificationToaster';
import React, { FC, useCallback, useEffect, useState } from 'react';
import Slider from 'src/components/widgets/slider';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { EnvironmentsIcon as CubeMapIcon } from '../../../../../widgets/icons/scene';

interface SettingsEditorProps {
    sceneEditor: any;
    zkScene: any;
    isAIEnabled: boolean;
    setAIEnabled: (enabled: boolean) => void;
    additionalFeatures?: any; // Aggiungi questa prop
    section?: 'all' | 'rendering' | 'composer' | 'behaviours';
}

const Container = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
`;

const Column = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
`;

const IndentedColumn = styled(Column)`
    padding-left: 24px;
`;

const DoubleIndentedColumn = styled(Column)`
    padding-left: 48px;
`;

const Row = styled.div<{ $indented?: number | boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    margin-left: ${(props) => {
        if (typeof props.$indented === 'number') {
            return `${props.$indented * 24}px`;
        }
        return props.$indented ? '24px' : '0';
    }};
`;

const Label = styled.label`
    font-size: 13px;
    color: #555;
    min-width: 120px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const NumberInputStyled = styled(Input)`
    width: 80px;
`;

const ColorPickerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ColorPreviewContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
`;

const ColorPreview = styled.div<{ color: string }>`
    width: 32px;
    height: 24px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: ${(props) => props.color};
    cursor: pointer;
    position: relative;
`;

const ColorHex = styled.span`
    font-family: monospace;
    font-size: 12px;
    color: #666;
    min-width: 70px;
    cursor: pointer;
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
`;

const ColorPickerPopover = styled.div`
    position: absolute;
    top: 100%;
    right: -50px;
    z-index: 1000;
    margin-top: 4px;
`;

const PreviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PreviewImage = styled.img`
    max-width: 100px;
    max-height: 100px;
    border: 1px solid #ddd;
    border-radius: 4px;
`;

const PreviewIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #f5f5f5;
    font-size: 12px;
    font-weight: bold;
    color: #666;

    &.cubemap {
        font-size: 20px;
    }

    i {
        font-size: 24px;
    }
`;

const TexturePreviewContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const SliderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex: 1;
`;

const SliderValueDisplay = styled.span`
    font-size: 11px;
    color: #666;
    min-width: 70px;
    text-align: right;
    font-family: monospace;
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
`;

// Helper function per ottenere gli URL delle texture come nel vecchio Settings
const getTextureUrls = (texture: any): string[] => {
    if (!texture) return [];

    // Se è già un array di stringhe (files dalla configurazione)
    if (Array.isArray(texture) && texture.length > 0 && typeof texture[0] === 'string') {
        return texture;
    }

    // Se è una texture Babylon con _texture
    if (texture._texture?.url) {
        return [texture._texture.url];
    }

    // Se ha direttamente un url
    if (texture.url) {
        return [texture.url];
    }

    // Se è un array di URL (cubemap)
    if (Array.isArray(texture)) {
        const urls = texture.map((t) => t.url || t._texture?.url).filter(Boolean);
        return urls;
    }

    // Se ha una proprietà files (cubemap)
    if (texture.files && Array.isArray(texture.files)) {
        return texture.files;
    }

    return [];
};

// Helper per la preview delle texture come nel vecchio Settings
const getPreview = (urls: string | string[] | null | undefined) => {
    if (!urls) return null;

    // Converti in array se è una stringa
    const urlArray = Array.isArray(urls) ? urls : [urls];
    if (urlArray.length === 0) return null;

    let preview: React.ReactElement;

    if (urlArray[0].endsWith('.hdr')) {
        preview = (
            <PreviewIcon>
                <span>HDR</span>
            </PreviewIcon>
        );
    } else if (urlArray[0].endsWith('.dds')) {
        preview = (
            <PreviewIcon>
                <span>DDS</span>
            </PreviewIcon>
        );
    } else if (urlArray[0].endsWith('.env')) {
        preview = (
            <PreviewIcon>
                <span>ENV</span>
            </PreviewIcon>
        );
    } else if (urlArray.length > 1 || (urlArray.length === 1 && !urlArray[0].split('/')?.pop()?.includes('.'))) {
        // Cubemap (più file)
        preview = (
            <PreviewIcon className='cubemap'>
                <CubeMapIcon />
            </PreviewIcon>
        );
    } else {
        // Immagine normale
        preview = <PreviewImage src={urlArray[0]} alt='Preview' />;
    }

    return preview;
};

const SettingsEditor: FC<SettingsEditorProps> = ({ sceneEditor, zkScene, isAIEnabled, setAIEnabled, additionalFeatures, section = 'all' }) => {
    // L'AMBIENTE HA TRASLOCATO. Texture d'ambiente, skybox e la sua sfocatura stavano qui
    // perché non avevano altro posto; ora la loro casa è la sezione **World**, dove stanno
    // insieme allo sfondo e al piano — cioè insieme alle altre cose che circondano il
    // modello. La procedura di caricamento è in `hooks/useEnvironmentTextures`.
    //
    // Resta ospite «Info Points»: la sua casa saranno i trigger point, che non esistono
    // ancora (docs/scene-editor-new-sections.md § 3). Il giorno che esistono, trasloca anche
    // lui e questa riga torna `section === 'all'`.
    const showInfoPoints = section === 'all' || section === 'behaviours';
    const showRendering = section === 'all' || section === 'rendering';
    const showComposer = section === 'all' || section === 'composer';
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // State for scene configuration
    const [infoPointsBorderColor, setInfoPointsBorderColor] = useState<string>('#ffffff');
    const [infoPointsColor, setInfoPointsColor] = useState<string>('#ffffff');
    const [infoPointsHighlightColor, setInfoPointsHighlightColor] = useState<string>('#ff0000');
    const [infoPointsInnerPointColor, setInfoPointsInnerPointColor] = useState<string>('#000000');
    const [enableAntialiasing, setEnableAntialiasing] = useState<boolean>(true);
    const [antialiasDesktop, setAntialiasDesktop] = useState<boolean>(true);
    const [antialiasMobile, setAntialiasMobile] = useState<boolean>(true);
    const [antialiasDesktopInRotation, setAntialiasDesktopInRotation] = useState<boolean>(false);
    const [antialiasMobileInRotation, setAntialiasMobileInRotation] = useState<boolean>(false);
    const [webpCompressionType, setWebpCompressionType] = useState<string>('Lossy');
    const [webpCompressionQuality, setWebpCompressionQuality] = useState<number>(80);
    // const [dracoCompression, setDracoCompression] = useState<boolean>(false);
    const [enableAI, setEnableAI] = useState<boolean>(false);
    const [optimizeTextureManagement, setOptimizeTextureManagement] = useState<boolean>(false);
    const [enableWebP, setEnableWebP] = useState<boolean>(false);
    const [useDelayedMaterialsLoading, setUseDelayedMaterialsLoading] = useState<boolean>(false);
    const [infoPointsEnabled, setInfoPointsEnabled] = useState<boolean>(false);
    const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);

    const getAntiAliasConfig = useCallback(() => {
        try {
            const antiAliasConfig = sceneEditor?.getSceneConfig()?.toJSON()?.general?.antialias;
            if (!antiAliasConfig) {
                return {
                    desktop: {
                        enabled: false,
                        inRotationEnabled: false
                    },
                    mobile: {
                        enabled: false,
                        inRotationEnabled: false
                    }
                };
            }
            return antiAliasConfig;
        } catch (error) {
            console.error('Error getting antialias config:', error);
            return {
                desktop: {
                    enabled: false,
                    inRotationEnabled: false
                },
                mobile: {
                    enabled: false,
                    inRotationEnabled: false
                }
            };
        }
    }, [sceneEditor]);

    const setAntiAliasConfig = useCallback(
        (config: any) => {
            try {
                const sceneConfig = sceneEditor?.getSceneConfig();
                if (sceneConfig) {
                    const generalConfig = sceneConfig.get('general');
                    if (generalConfig && (window as any).Zakeke?.SceneAntiAliasingConfig) {
                        generalConfig.set('antialias', new (window as any).Zakeke.SceneAntiAliasingConfig(config));
                    }
                }
            } catch (error) {
                console.error('Error setting antialias config:', error);
            }
        },
        [sceneEditor]
    );

    // Helper functions seguendo la logica del vecchio SceneEditorPage
    const setWebPEnabled = useCallback(
        (enabled: boolean) => {
            setEnableWebP(enabled);
            zkScene?.setWebPEnabled?.(enabled);
        },
        [zkScene]
    );

    const setWebPType = useCallback(
        (type: string) => {
            setWebpCompressionType(type);
            zkScene?.setWebPType?.(type);
        },
        [zkScene]
    );

    const setWebPQuality = useCallback(
        (quality: number) => {
            setWebpCompressionQuality(quality);
            zkScene?.setWebPQuality?.(quality);
        },
        [zkScene]
    );

    const setMatchSameNameTextures = useCallback(
        (enabled: boolean) => {
            setOptimizeTextureManagement(enabled);
            sceneEditor?.setMatchSameNameTextures?.(enabled);
        },
        [sceneEditor]
    );

    const setUseDelayedMaterialLoading = useCallback(
        (enabled: boolean) => {
            setUseDelayedMaterialsLoading(enabled);
            sceneEditor?.setUseDelayedMaterialLoading?.(enabled);
        },
        [sceneEditor]
    );

    const setInfoPointsEnabledHandler = useCallback(
        (enabled: boolean) => {
            setInfoPointsEnabled(enabled);
            sceneEditor!.setInfoPointsEnabled(enabled);
        },
        [sceneEditor, zkScene]
    );

    const setAiConfigurationEnabled = useCallback(
        (enabled: boolean) => {
            // Verifica se la feature AI Powered Tools è disponibile
            if (enabled && !additionalFeatures?.aiPoweredTools) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'AI Powered Tools')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            setEnableAI(enabled);
            setAIEnabled(enabled);
        },
        [additionalFeatures, showDialog, sellerMarketplace, updateSubscription, setEnableAI, setAIEnabled]
    );

    // Load scene configuration
    useEffect(() => {
        if (sceneEditor && zkScene) {
            try {
                const config = sceneEditor.getSceneConfig();

                // Load current values from backbone model
                if (config) {
                    // Info Points
                    setInfoPointsEnabled(config.get('infoPoints')?.get('enabled') ?? true);
                    const infoPointsConfig = config.get('infoPoints')?.get('pointsStyle');
                    if (infoPointsConfig) {
                        setInfoPointsColor(infoPointsConfig.get?.('color') || '#ffffff');
                        setInfoPointsBorderColor(infoPointsConfig.get?.('borderColor') || '#ffffff');
                        setInfoPointsHighlightColor(infoPointsConfig.get?.('highlightColor') || '#ff0000');
                        setInfoPointsInnerPointColor(infoPointsConfig.get?.('innerPointColor') || '#000000');
                    }

                    // Antialias - usando la nuova logica
                    const antialiasConfig = getAntiAliasConfig();
                    if (antialiasConfig) {
                        // Il parent è abilitato se almeno uno dei device è abilitato
                        const parentEnabled = antialiasConfig.desktop?.enabled || antialiasConfig.mobile?.enabled;
                        setEnableAntialiasing(parentEnabled);

                        setAntialiasDesktop(antialiasConfig.desktop?.enabled || false);
                        setAntialiasDesktopInRotation(antialiasConfig.desktop?.inRotationEnabled || false);
                        setAntialiasMobile(antialiasConfig.mobile?.enabled || false);
                        setAntialiasMobileInRotation(antialiasConfig.mobile?.inRotationEnabled || false);
                    }

                    // WebP Compression - usando zkScene
                    setEnableWebP(zkScene?.getWebPEnabled?.() ?? false);
                    setWebpCompressionType(zkScene?.getWebPType?.() ?? 'Lossy');
                    setWebpCompressionQuality(zkScene?.getWebPQuality?.() ?? 80);

                    // Altri settings usando sceneEditor
                    setOptimizeTextureManagement(sceneEditor?.getMatchSameNameTextures?.() ?? false);
                    setUseDelayedMaterialsLoading(sceneEditor?.getUseDelayedMaterialLoading?.() ?? false);

                    // Other settings dal config
                    // setDracoCompression(zkScene.getForceDracoCompression() === true);
                    setEnableAI(isAIEnabled ?? true);
                }
            } catch (error) {}
        }
    }, [sceneEditor, zkScene, getAntiAliasConfig]);

    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openColorPicker && !(event.target as Element)?.closest('.color-picker-container')) {
                setOpenColorPicker(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openColorPicker]);

    const handleInfoPointsColorChange = useCallback(
        (colorType: 'borderColor' | 'color' | 'highlightColor' | 'innerPointColor', color: string) => {
            switch (colorType) {
                case 'color':
                    setInfoPointsColor(color);
                    sceneEditor?.setInfoPointsColor?.(color);
                    break;
                case 'borderColor':
                    setInfoPointsBorderColor(color);
                    sceneEditor?.setInfoPointsBorderColor?.(color);
                    break;
                case 'color':
                    // This seems to be a legacy property - might not be needed
                    break;
                case 'highlightColor':
                    setInfoPointsHighlightColor(color);
                    sceneEditor?.setInfoPointsHighlightColor?.(color);
                    break;
                case 'innerPointColor':
                    setInfoPointsInnerPointColor(color);
                    sceneEditor?.setInfoPointsInnerPointColor?.(color);
                    break;
            }

            // Close the color picker after selection
            // setOpenColorPicker(null);
        },
        [sceneEditor]
    );

    const handleEnableAntialiasing = useCallback(
        (enabled: boolean) => {
            setEnableAntialiasing(enabled);

            // Se disabilito il parent, disabilito anche tutti i figli
            if (!enabled) {
                setAntialiasDesktop(false);
                setAntialiasMobile(false);
                setAntialiasDesktopInRotation(false);
                setAntialiasMobileInRotation(false);
            }

            // Usa la nuova logica per aggiornare l'antialias config
            const currentConfig = getAntiAliasConfig();
            const newConfig = {
                ...currentConfig,
                desktop: {
                    enabled: enabled ? currentConfig.desktop?.enabled || false : false,
                    inRotationEnabled: enabled ? currentConfig.desktop?.inRotationEnabled || false : false
                },
                mobile: {
                    enabled: enabled ? currentConfig.mobile?.enabled || false : false,
                    inRotationEnabled: enabled ? currentConfig.mobile?.inRotationEnabled || false : false
                }
            };

            setAntiAliasConfig(newConfig);
        },
        [sceneEditor, getAntiAliasConfig, setAntiAliasConfig]
    );

    const handleAntialiasChange = useCallback(
        (device: 'desktop' | 'mobile', setting: 'enabled' | 'inRotation', value: boolean) => {
            // Aggiorna lo stato locale
            if (device === 'desktop') {
                if (setting === 'enabled') {
                    setAntialiasDesktop(value);
                    // Se disabilito desktop, disabilito anche inRotation
                    if (!value) {
                        setAntialiasDesktopInRotation(false);
                    }
                } else {
                    setAntialiasDesktopInRotation(value);
                    // Se abilito inRotation, devo abilitare anche desktop
                    if (value && !antialiasDesktop) {
                        setAntialiasDesktop(true);
                    }
                }
            } else {
                if (setting === 'enabled') {
                    setAntialiasMobile(value);
                    // Se disabilito mobile, disabilito anche inRotation
                    if (!value) {
                        setAntialiasMobileInRotation(false);
                    }
                } else {
                    setAntialiasMobileInRotation(value);
                    // Se abilito inRotation, devo abilitare anche mobile
                    if (value && !antialiasMobile) {
                        setAntialiasMobile(true);
                    }
                }
            }

            // Aggiorna la configurazione usando la nuova logica
            const currentConfig = getAntiAliasConfig();
            const newConfig = { ...currentConfig };

            if (device === 'desktop') {
                if (setting === 'enabled') {
                    newConfig.desktop = {
                        ...newConfig.desktop,
                        enabled: value,
                        inRotationEnabled: value ? newConfig.desktop?.inRotationEnabled || false : false
                    };
                } else {
                    // inRotation
                    newConfig.desktop = {
                        ...newConfig.desktop,
                        enabled: value ? true : newConfig.desktop?.enabled || false,
                        inRotationEnabled: value
                    };
                }
            } else {
                // mobile
                if (setting === 'enabled') {
                    newConfig.mobile = {
                        ...newConfig.mobile,
                        enabled: value,
                        inRotationEnabled: value ? newConfig.mobile?.inRotationEnabled || false : false
                    };
                } else {
                    // inRotation
                    newConfig.mobile = {
                        ...newConfig.mobile,
                        enabled: value ? true : newConfig.mobile?.enabled || false,
                        inRotationEnabled: value
                    };
                }
            }

            setAntiAliasConfig(newConfig);
        },
        [sceneEditor, antialiasDesktop, antialiasMobile, getAntiAliasConfig, setAntiAliasConfig]
    );

    // Helper component for color properties like in material editor
    const ColorProperty: FC<{
        id: string;
        label: string;
        value: string;
        onChange: (color: string) => void;
        tooltip?: string;
    }> = ({ id, label, value, onChange, tooltip }) => {
        const isOpen = openColorPicker === id;

        return (
            <Column>
                <Row>
                    <Label>{label}</Label>
                    <ColorPreviewContainer>
                        <ColorPreview color={value} onClick={() => setOpenColorPicker(id)}>
                            {isOpen && (
                                <ColorPickerPopover className='color-picker-container'>
                                    <ColorPicker color={value} onChange={onChange} />
                                </ColorPickerPopover>
                            )}
                        </ColorPreview>
                        <ColorHex onClick={() => setOpenColorPicker(id)}>{value}</ColorHex>
                    </ColorPreviewContainer>
                </Row>
            </Column>
        );
    };

    return (
        <Container>
            {/* Environment Section */}
            {/* Antialias Section */}
            {showRendering && (
                <Section>
                    <SectionTitle>{T._('Anti-aliasing', 'Admin')}</SectionTitle>

                    {/* Parent Enable Antialias Checkbox */}
                    <Column>
                        <FormControl>
                            <Checkbox label={T._('Enabled', 'Admin')} checked={enableAntialiasing} onChange={handleEnableAntialiasing} />
                        </FormControl>
                    </Column>

                    {/* Desktop Antialias - Level 2 */}
                    {enableAntialiasing && (
                        <IndentedColumn>
                            <FormControl>
                                <Checkbox
                                    label={T._('Desktop', 'Admin')}
                                    checked={antialiasDesktop}
                                    onChange={(checked) => handleAntialiasChange('desktop', 'enabled', checked)}
                                />
                            </FormControl>
                        </IndentedColumn>
                    )}

                    {/* Desktop In Rotation - Level 3 */}
                    {enableAntialiasing && antialiasDesktop && (
                        <DoubleIndentedColumn>
                            <FormControl>
                                <Checkbox
                                    label={T._('Desktop while rotating', 'Admin')}
                                    checked={antialiasDesktopInRotation}
                                    onChange={(checked) => handleAntialiasChange('desktop', 'inRotation', checked)}
                                />
                            </FormControl>
                        </DoubleIndentedColumn>
                    )}

                    {/* Mobile Antialias - Level 2 */}
                    {enableAntialiasing && (
                        <IndentedColumn>
                            <FormControl>
                                <Checkbox
                                    label={T._('Mobile', 'Admin')}
                                    checked={antialiasMobile}
                                    onChange={(checked) => handleAntialiasChange('mobile', 'enabled', checked)}
                                />
                            </FormControl>
                        </IndentedColumn>
                    )}

                    {/* Mobile In Rotation - Level 3 */}
                    {enableAntialiasing && antialiasMobile && (
                        <DoubleIndentedColumn>
                            <FormControl>
                                <Checkbox
                                    label={T._('Mobile while rotating', 'Admin')}
                                    checked={antialiasMobileInRotation}
                                    onChange={(checked) => handleAntialiasChange('mobile', 'inRotation', checked)}
                                />
                            </FormControl>
                        </DoubleIndentedColumn>
                    )}
                </Section>
            )}

            {/* Info Points Section */}
            {showInfoPoints && (
                <Section>
                    <SectionTitle>{T._('Info points', 'Admin')}</SectionTitle>

                    <Column>
                        <FormControl>
                            <Checkbox
                                label={T._('Info points', 'Admin')}
                                checked={infoPointsEnabled}
                                onChange={setInfoPointsEnabledHandler}
                                helpTooltip={
                                    <>
                                        {T._('Enable your customers to see high-res 2D images of the product on 3D model. ', 'Composer')}
                                        <br />
                                        <a
                                            style={{ color: '#f78200', textDecoration: 'underline' }}
                                            href='https://zakeke.zendesk.com/hc/en-us/articles/360014772579'
                                            target='_blank'
                                        >
                                            {T._('Learn more', 'Composer')}
                                        </a>
                                    </>
                                }
                            />
                        </FormControl>
                    </Column>

                    {infoPointsEnabled && (
                        <>
                            <ColorProperty
                                id='color'
                                label={T._('Color', 'Admin')}
                                value={infoPointsColor}
                                onChange={(color) => handleInfoPointsColorChange('color', color)}
                            />
                            <ColorProperty
                                id='borderColor'
                                label={T._('Border color', 'Admin')}
                                value={infoPointsBorderColor}
                                onChange={(color) => handleInfoPointsColorChange('borderColor', color)}
                            />

                            <ColorProperty
                                id='highlightColor'
                                label={T._('Highlight color', 'Admin')}
                                value={infoPointsHighlightColor}
                                onChange={(color) => handleInfoPointsColorChange('highlightColor', color)}
                            />

                            <ColorProperty
                                id='innerPointColor'
                                label={T._('Inner point color', 'Admin')}
                                value={infoPointsInnerPointColor}
                                onChange={(color) => handleInfoPointsColorChange('innerPointColor', color)}
                            />
                        </>
                    )}
                </Section>
            )}

            {/* Other Settings Section */}
            {(showRendering || showComposer) && (
                <Section>
                    <SectionTitle>{T._('Other settings', 'Admin')}</SectionTitle>

                    {showRendering && (
                        <>
                            {/* Optimize Texture Management */}
                            <Column>
                                <FormControl>
                                    <Checkbox
                                        label={T._('Optimize texture management', 'Admin')}
                                        checked={optimizeTextureManagement}
                                        onChange={setMatchSameNameTextures}
                                        helpTooltip={T._(
                                            'Effortlessly merge textures with identical names into a single texture to optimize performance. When you upload the textures with the same names on multiple materials, Zakeke automatically consolidates them, allowing the browser to download it just once. This optimization minimizes loading times and boosts overall performance, providing a faster and smoother 3D product experience for your customers.',
                                            'Admin'
                                        )}
                                    />
                                </FormControl>
                            </Column>

                            {/* Enable WebP for Textures */}
                            <Column>
                                <FormControl>
                                    <Checkbox
                                        label={T._('WebP textures', 'Admin')}
                                        checked={enableWebP}
                                        onChange={setWebPEnabled}
                                        helpTooltip={
                                            <span>
                                                {T._('Enable WebP to optimize texture file sizes. You have two options: ', 'Composer')}
                                                <div>
                                                    <strong>{T._('• Lossy: ', 'Composer')}</strong>
                                                    {T._(
                                                        'Heavily compresses textures with some quality loss. You can customize the quality level.',
                                                        'Composer'
                                                    )}
                                                </div>
                                                <div>
                                                    <strong>{T._('• Lossless: ', 'Composer')}</strong>
                                                    {T._('Minimally compresses textures to maintain original quality.', 'Composer')}
                                                </div>
                                                <a
                                                    target='_blank'
                                                    style={{ color: '#f78200', textDecoration: 'underline' }}
                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/14248548502940-WebP-for-Textures'
                                                >
                                                    {' '}
                                                    We recommend delving deeper here.
                                                </a>
                                            </span>
                                        }
                                    />
                                </FormControl>
                            </Column>

                            {enableWebP && (
                                <>
                                    <Column>
                                        <Label>{T._('Compression type', 'Admin')}</Label>
                                        <AdvancedSelect
                                            value={{ value: webpCompressionType, label: T._(webpCompressionType, 'Admin') }}
                                            options={[
                                                { label: T._('Lossy', 'Admin'), value: 'Lossy' },
                                                { label: T._('Lossless', 'Admin'), value: 'Lossless' }
                                            ]}
                                            onChange={(value) => value && setWebPType(value.value)}
                                        />
                                    </Column>
                                    {webpCompressionType === 'Lossy' && (
                                        <Column>
                                            <Label>{T._('Quality', 'Admin')}</Label>
                                            <Slider min={1} max={100} value={webpCompressionQuality} onChange={(val) => setWebPQuality(Number(val))} />
                                        </Column>
                                    )}
                                </>
                            )}

                            {/* Use delayed materials loading */}
                            <Column>
                                <FormControl>
                                    <Checkbox
                                        label={T._('Delayed materials loading', 'Admin')}
                                        checked={useDelayedMaterialsLoading}
                                        onChange={setUseDelayedMaterialLoading}
                                    />
                                </FormControl>
                            </Column>
                        </>
                    )}

                    {showComposer && (
                        <Column>
                            <FormControl>
                                <Checkbox
                                    label={T._('AI product configuration generator', 'Admin')}
                                    checked={enableAI}
                                    onChange={(checked) => {
                                        setAiConfigurationEnabled(checked);
                                    }}
                                    helpTooltip={
                                        <>
                                            {T._(
                                                "Enhance your product customization experience with our AI-Powered Configuration Generator. Your clients simply describe their desired configuration and our advanced AI technology automatically generates the perfect customized product for them. No manual option selection, our intelligent system handles the permutations and delivers a tailored configuration that precisely matches your customers' preferences.",
                                                'Composer'
                                            )}
                                            <br />
                                            <a
                                                target='_blank'
                                                style={{ color: '#f78200', textDecoration: 'underline' }}
                                                href='https://zakeke.zendesk.com/hc/en-us/articles/9649789714460-AI-Configuration'
                                            >
                                                {T._('Learn more', 'Composer')}
                                            </a>
                                        </>
                                    }
                                />
                            </FormControl>
                        </Column>
                    )}

                    {/* <Column>
					<FormControl>
						<Checkbox
							label={T._('Draco compression', 'Admin')}
							checked={dracoCompression}
							onChange={(checked) => {
								setDracoCompression(checked);
								try {
									sceneEditor.setForceDracoCompression(checked);
								} catch (error) {
									console.error('Error updating draco compression:', error);
								}
							}}
							helpTooltip={T._('Enable Draco geometry compression', 'Admin')}
						/>
					</FormControl>
				</Column> */}
                </Section>
            )}
        </Container>
    );
};

export default SettingsEditor;
